import * as _ from 'lodash';
import { useMemo, useState } from 'react';
import { Utility } from '@nkr/services/utility';
import { ISwapDonor } from '@nkr/models/index';
import { DonationType, DonorAction, MatchStatus, MenuLocation } from '@nkr/enums/index';
import { BodyScrollEvent, CellClickedEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Glyphicon, Label } from 'react-bootstrap';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { GridWrapper } from './styles';
import ActionButtonRenderer from './action-button-renderer';

interface IProps {
    list: ISwapDonor[],
    centerId: number,
    isAdmin: boolean,
    isSuper: boolean,
    onAction: (id: number, action: DonorAction) => void,
    onView: (id: number) => void
}

const donationTypeMap = {
    [DonationType.DirectedDonor]: { abbr: 'Paired', style: 'default' },
    [DonationType.Bridge]: { abbr: 'Bridge', style: 'default' },
    [DonationType.NonDirectedDonor]: { abbr: 'NDD', style: 'warning' },
    [DonationType.StandardVoucher]: { abbr: 'SV', style: 'success' },
    [DonationType.FamilyVoucher]: { abbr: 'FV', style: 'success' },
    [DonationType.SwapSaver]: { abbr: 'FV', style: 'success' },
    [DonationType.RealTimeSwapFailure]: { abbr: 'RTSF', style: 'success' },
    [DonationType.RemoteDirect]: { abbr: 'RD', style: 'info' },
    [DonationType.KflInternalDirect]: { abbr: 'KFL ID', style: 'info' },
    [DonationType.KflRemoteDirect]: { abbr: 'KFL RD', style: 'info' },
    [DonationType.NkrInternalDirect]: { abbr: 'NKR ID', style: 'info' },
};

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
        e.columnApi.applyColumnState({
            state: [
                {
                    colId: 'exportability',
                    sort: 'asc'
                }
            ]
        });

        e.api.sizeColumnsToFit();
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChange = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    }

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const columns: ColDef[] = [
        {
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>,
            headerName: 'R',
            width: 60,
            sortable: false,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },
        {
            field: 'centerAlias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            headerName: 'centerAlias',
            width: 150,
            cellRendererFramework: (params: ICellRendererParams) => <a href={`/center/${params.data.centerId}`}>{params.data.centerAlias}</a>,
            hide: !props.isAdmin ? true : false,
            comparator: Utility.nameComparator,
        },

        {
            field: 'donationTypeId',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            headerName: 'Type',
            width: 105,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISwapDonor;
                let t = donationTypeMap[data.donationTypeId];

                return (
                    <Label bsStyle={t.style} className='label-full'>{t.abbr}</Label>
                );
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ISwapDonor;
                let t = donationTypeMap[data.donationTypeId];

                return t.abbr;
            }
        },
        {
            field: 'registered',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Registered</SortableHeaderComponent>,
            headerName: 'Registered',
            width: 120,
            comparator: Utility.dateComparator
        },
        {
            field: 'donated',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donated</SortableHeaderComponent>,
            headerName: 'Donated',
            width: 120,
            comparator: Utility.dateComparator
        },
        {
            field: 'fullName',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Name</SortableHeaderComponent>,
            headerName: 'Name',
            width: 170,
        },
        {
            field: 'alias',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Alias</SortableHeaderComponent>,
            headerName: 'Alias',
            width: 130,
        },
        {
            field: 'gender',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Gender</SortableHeaderComponent>,
            headerName: 'Gender',
            width: 70,
        },
        {
            field: 'age',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Age</SortableHeaderComponent>,
            headerName: 'Age',
            width: 60,
        },
        {
            field: 'bloodType',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Blood<br />Type</SortableHeaderComponent>,
            headerName: 'Blood Type',
            width: 60,
        },
        {
            field: 'status',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            headerName: 'Status',
            width: 230,
        },
        {
            field: 'wageAndTravelRequested',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Wage &amp;<br />Expense</SortableHeaderComponent>,
            headerName: 'Wage/Travel',
            width: 80,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISwapDonor;

                if (data.wageAndTravelRequested)
                    return (
                        <Glyphicon glyph='ok' />
                    );

                return null;
            },
        },

        {
            field: 'remoteRecipientCenter',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Remote Patient<br />Center</SortableHeaderComponent>,
            headerName: 'Remote Donor',
            width: 130
        },
        {
            field: 'registrationId',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>DASH</SortableHeaderComponent>,
            headerName: 'DASH',
            width: 70,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISwapDonor;

                if (data.registrationId > 0)
                    return (
                        <Glyphicon glyph='ok' />
                    );

                return null;
            },
        },
        {
            field: 'isExpertSystem',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Expert <br /> System</SortableHeaderComponent>,
            headerName: 'Expert System',
            width: 70,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISwapDonor;

                if (data.isExpertSystem)
                    return (
                        <Glyphicon glyph='ok' />
                    );

                return null;
            },
        },
        {
            field: 'exportability',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Exportability</SortableHeaderComponent>,
            headerName: 'Exportability',
            width: 130,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISwapDonor;

                if (!data.isActive && data.statusId == MatchStatus.NeedsMatch) {
                    return (
                        <Label bsStyle='default' className='label-full' title='Patient is inactive and will not be matched'>inactive</Label>
                    );
                }
                if (!data.isActive && data.statusId != MatchStatus.Transplanted) {
                    return (
                        <Label bsStyle='default' className='label-full' title='Patient is inactive'>inactive</Label>
                    );
                }

                if (data.statusId > MatchStatus.NeedsMatch) {
                    if (data.statusId == MatchStatus.Transplanted) {
                        return (
                            <Label bsStyle='primary' className='label-full' title='Patient is transplanted'>xplanted</Label>
                        );
                    } else {
                        return (
                            <Label bsStyle='info' className='label-full' title='Patient is already in a match'>in match</Label>
                        );
                    }
                } else {
                    if (data.isExportable) {
                        return (
                            <Label bsStyle='success' className='label-full' title='Patient is active and all required data is completed to receive a match offer'>matchable</Label>
                        );
                    } else {
                        return (
                            <Label bsStyle='danger' className='label-full' title='Patient is active, but NOT all data required to receive a match offer has been entered'>incomplete</Label>
                        );
                    }
                }
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ISwapDonor;

                if (!data.isActive && data.statusId == MatchStatus.NeedsMatch) {  // inactive
                    return 5;
                }
                if (!data.isActive && data.statusId != MatchStatus.Transplanted) {  // also inactive
                    return 6;
                }
                if (data.statusId > MatchStatus.NeedsMatch) {
                    if (data.statusId == MatchStatus.Transplanted) {
                        return 1;   // xplanted
                    } else {
                        return 2;   // in match
                    }
                } else {
                    if (data.isExportable) {
                        return 3;   // matchable
                    } else {
                        return 4;   // incomplete
                    }
                }
            }
        },
        {
            headerName: 'Voucher',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Voucher</SortableHeaderComponent>,
            width: 100,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISwapDonor;

                if (!data.isAdp) {
                    return (
                        <Label bsStyle='default' className='label-full'>N/A</Label>

                    );
                }

                if (data.adpVoucherHolders.length > 0) {
                    return (
                        <Label bsStyle='success' style={{ fontSize: '90%', width: '100%' }}>{data.adpVoucherHolders.length} avail</Label>
                    );
                }

                return <Label bsStyle={data.adpVoucherHolders.length == 0 && data.isExportable ? 'danger' : 'warning'} style={{ fontSize: '90%', width: '100%' }}> 0 avail</Label>
            },
            valueGetter: (params: ValueGetterParams) => {
                let data = params.data as ISwapDonor;

                if (!data.isAdp) {
                    return -1;
                }

                if (data.adpVoucherHolders.length > 0) {
                    return data.adpVoucherHolders.length;
                }

                return 0;
            }
        },
        {
            field: 'actionId',
            headerName: '',
            width: 90,
            cellClass: 'action-menu',
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ISwapDonor;
                let t = donationTypeMap[data.donationTypeId];

                return (
                    <ActionButtonRenderer
                        {...params}

                        orderId={data.requisitionId}
                        donorId={data.donorId}
                        centerId={props.centerId}
                        donorCenterId={data.centerId}
                        donationCenterId={data.donationCenterId}
                        canDelete={data.canDelete}
                        canConvert={data.canConvert}
                        isAdp={data.isAdp}
                        donorType={t.abbr}
                        statusId={data.statusId}
                        wageAndTravelRequested={data.wageAndTravelRequested}
                        registrationId={data.registrationId}
                        recipientId={data.recipientId}
                        recipientCenterId={data.recipientCenterId}
                        recipientType={data.recipientType}
                        menuLocation={MenuLocation.List}
                        isAdmin={props.isAdmin}
                        isSuper={props.isSuper}
                        canOrderCheekSwab={data.canOrderCheekSwab}
                        onMenuToggle={handleMenuToggle}
                        onItemSelectedInList={props.onAction}
                    />
                )
            }
        }
    ]

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.list, [props.list]);

    return (
        <GridWrapper className='ag-theme-bootstrap' isAdmin={props.isAdmin} style={{ height: props.isAdmin ? '60vh' : '46vh' }}>
            <AgGridReact
                defaultColDef={{
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    sortable: true
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={42}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                enableCellTextSelection={true}
                suppressContextMenu={true}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChange}
                onSortChanged={onSortChanged}
                onGridSizeChanged={(e: GridSizeChangedEvent) => e.api.sizeColumnsToFit()}
                onCellClicked={(e: CellClickedEvent) => {
                    if (e.colDef.field != 'alias' && e.colDef.field != 'actionId') {
                        let { donorId } = (e.data as ISwapDonor);
                        props.onView(donorId);
                    }
                }}
                onBodyScroll={onBodyScroll}
                noRowsOverlayComponentFramework={() => <>No donors found...</>} />
        </GridWrapper>
    );
}

export default Grid;